// Authenticity Promise card: the store's money-backed guarantee, shown as a card right under the
// home search bar so it is seen without scrolling (sir's call, 5 Nov 2026: "above the fold").
//
// It replaces the one-line green AuthenticityPromiseBar on the home page. Design "A · How it works"
// from the home-trust demo, made compact: header, the four checks, three steps, and a link to the
// full terms. All three steps are always visible (no expand).
//
// HEIGHT BUDGET (phone): the card fills the space down to the festival banner, so the banner's
// bottom sits just above the screen edge on a 390x844 phone (most common iPhone size; ~266px card).
// Aayush, 5 Nov. To stay in that budget: checks are one-line chips and each step line fits ONE
// line at 375px. Measure (getBoundingClientRect) before adding anything here.
//
// The copy states nothing the full promise doesn't: every claim here comes from authSheet
// (four checks, NABL lab, 45 days, refund plus testing cost up to the cap). The full terms open in
// the shared AuthenticityPromiseSheet, so the home card and the PDP badge can never disagree.
//
// No request and no images: the store name comes from Redux storeDetails (fetched once in App.js),
// the ticks are text. It is above the fold, so it is NOT lazy-loaded.

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AuthenticityPromiseSheet, {
  CLAIM_CAP,
  CLAIM_WINDOW_DAYS,
} from "../AuthenticityPromiseSheet";

// Same fallback the other store-name consumers use (AuthenticityPromiseBar, StoreFooter).
const BRAND_FALLBACK = "The Crafts Archive";

const CHECKS = ["fabric", "yarn", "silk", "zari"];
const STEPS = ["verified", "test", "refund"];

export default function AuthenticityPromiseCard() {
  const { t } = useTranslation();
  const [sheet, setSheet] = useState(false);

  // Never hardcode the brand: one build serves every store.
  const brand = useSelector((s) => s.storeDetails?.storeName) || BRAND_FALLBACK;
  const values = { brand, days: CLAIM_WINDOW_DAYS, cap: CLAIM_CAP };

  return (
    <>
      <section
        aria-labelledby="auth-card-title"
        className="mx-4 mb-4 rounded-2xl border border-[#D6E9DC] bg-[#F3F8F4] px-[13px] py-[12px] text-ink-900 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:px-6 lg:py-5"
      >
        {/* Desktop: two columns (promise + checks | steps). Mobile: one column. */}
        <div className="min-w-0">
          <p className="text-[9.5px] font-[500] uppercase tracking-[.14em] text-[#1E6B45]">
            {t("authCard.eyebrow", values)}
          </p>
          <h2
            id="auth-card-title"
            className="mt-[4px] text-[16px] font-[500] leading-[1.25]"
          >
            {t("authCard.title")}
          </h2>
          <p className="mt-[3px] text-[11.5px] leading-[1.45] text-[#545454]">
            {t("authCard.subtitle")}
          </p>

          <ul className="my-[9px] grid grid-cols-4 gap-[6px] lg:mb-0 lg:mt-[12px]">
            {CHECKS.map((c) => (
              <li
                key={c}
                className="flex items-center justify-center gap-[4px] rounded-[9px] border border-[#D6E9DC] bg-white py-[7px] text-[11px] font-[500]"
              >
                <span aria-hidden="true" className="text-[11px] text-[#1E6B45]">
                  ✓
                </span>
                <span>{t(`authCard.checks.${c}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="min-w-0 lg:border-l lg:border-[#D6E9DC] lg:pl-8">
          <ol>
            {STEPS.map((s, i) => (
              <li
                key={s}
                className={`relative flex gap-[9px] ${i < STEPS.length - 1 ? "pb-[7px]" : ""}`}
              >
                {/* line joining the numbered dots */}
                {i < STEPS.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="absolute bottom-0 left-[9.5px] top-[20px] w-px bg-[#B8D8C3]"
                  />
                )}
                <span
                  aria-hidden="true"
                  className="relative z-[1] grid h-[20px] w-[20px] flex-none place-items-center rounded-full bg-[#1E6B45] text-[10.5px] font-[500] text-white"
                >
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-[12px] font-[500] leading-[1.3]">
                    {t(`authCard.steps.${s}.title`, values)}
                  </p>
                  <p className="text-[11px] leading-[1.35] text-[#545454]">
                    {t(`authCard.steps.${s}.line`, values)}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <button
            type="button"
            onClick={() => setSheet(true)}
            aria-haspopup="dialog"
            className="mt-[8px] border-0 bg-transparent p-0 text-[11.5px] font-[500] text-[#1E6B45] underline"
          >
            {t("authCard.readMore")}
          </button>
        </div>
      </section>

      <AuthenticityPromiseSheet sheet={sheet} setSheet={setSheet} brand={brand} />
    </>
  );
}
